// Per-outlet menu list: menus for this outlet (slice 4), the live
// pointer status (slice 6), and the three mutations the owner needs
// most — create menu, publish, roll back.
//
// Slice 16: buttons hide for roles that cannot perform the action
// (the server stays authoritative via PermissionGuard — see
// apps/admin/src/auth/permissions.ts), and the menu-status query
// polls every 3 s so the rollback button enables itself after the
// worker flips the pointer. No more page.reload() in the e2e.

import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../api/client.js';
import type {
  MenuStatus,
  MenuSummary,
  MeResponse,
  PublishResponse,
  RollbackResponse,
} from '../api/types.js';
import { useOutletPermissions, hasPermission } from '../auth/permissions.js';
import { LiveSince } from './components/PublishPanel.js';

// Menu-status cache has no refetchInterval on invalidation (publish's
// onSuccess fires BEFORE the worker flips the pointer); poll every
// 3 s so the UI catches up without a manual reload. The admin is
// authenticated and this endpoint is one small row — the extra load
// is negligible.
const MENU_STATUS_POLL_MS = 3000;

export function MenuListPage(): JSX.Element {
  const { orgId = '', outletId = '' } = useParams();
  const qc = useQueryClient();

  // The home page populates ['me'] with staleTime 30s; a deep link or
  // tab refresh may land here with the cache empty. Fire the same
  // query from here so permissions resolve either way. TanStack
  // dedupes by key, so no extra request.
  useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });

  const permissions = useOutletPermissions(orgId, outletId);
  const canEdit = hasPermission(permissions, 'menu:edit');
  const canPublish = hasPermission(permissions, 'menu:publish');

  const menusKey = ['menus', orgId, outletId] as const;
  const statusKey = ['menu-status', orgId, outletId] as const;

  const menus = useQuery({
    queryKey: menusKey,
    queryFn: () =>
      apiFetch<{ menus: MenuSummary[] }>(`/organizations/${orgId}/outlets/${outletId}/menus`),
  });

  const status = useQuery({
    queryKey: statusKey,
    queryFn: () => apiFetch<MenuStatus>(`/organizations/${orgId}/outlets/${outletId}/menu-status`),
    refetchInterval: MENU_STATUS_POLL_MS,
    refetchIntervalInBackground: false,
  });

  const createMenu = useMutation({
    mutationFn: (body: { slug: string; name: string }) =>
      apiFetch<MenuSummary>(`/organizations/${orgId}/menus`, {
        method: 'POST',
        body: { ...body, outletId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: menusKey }),
  });

  const publish = useMutation({
    mutationFn: (args: { menuId: string; expectedVersion: number }) =>
      apiFetch<PublishResponse>(`/organizations/${orgId}/menus/${args.menuId}/publish`, {
        method: 'POST',
        body: { expectedVersion: args.expectedVersion },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: menusKey });
      qc.invalidateQueries({ queryKey: statusKey });
    },
  });

  const rollback = useMutation({
    mutationFn: () =>
      apiFetch<RollbackResponse>(`/organizations/${orgId}/outlets/${outletId}/menu-rollback`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: statusKey }),
  });

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');

  function onCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    createMenu.mutate(
      { slug: slug.trim(), name: name.trim() },
      {
        onSuccess: () => {
          setSlug('');
          setName('');
        },
      },
    );
  }

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Menus</h1>
          <p className="muted">
            <Link to="/home">← all outlets</Link>
          </p>
        </div>
      </header>

      <section className="card">
        <h2>Live pointer</h2>
        {status.isLoading ? <p>Loading pointer…</p> : null}
        {status.data ? (
          status.data.current ? (
            <p>
              <strong>v{status.data.current.menuVersion}</strong> is live (pointer v
              {status.data.pointerVersion}).{' '}
              <span className="muted">
                <LiveSince status={status.data} />.
              </span>{' '}
              {status.data.previous ? (
                <span className="muted">Previous: v{status.data.previous.menuVersion}.</span>
              ) : null}
            </p>
          ) : (
            <p className="muted">No menu has been published yet.</p>
          )
        ) : null}
        {canPublish ? (
          <div className="row">
            <button
              type="button"
              disabled={!status.data?.previous || rollback.isPending}
              onClick={() => rollback.mutate()}
            >
              {rollback.isPending ? 'Rolling back…' : 'Roll back to previous'}
            </button>
            {rollback.error ? (
              <span className="error">{errorMessage(rollback.error, 'rollback failed')}</span>
            ) : null}
          </div>
        ) : null}
      </section>

      {canEdit ? (
        <section className="card">
          <h2>Create menu</h2>
          <form onSubmit={onCreate}>
            <label htmlFor="menu-slug">Slug</label>
            <input
              id="menu-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="dinner"
              required
            />
            <label htmlFor="menu-name">Name</label>
            <input
              id="menu-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dinner menu"
              required
            />
            <button type="submit" disabled={createMenu.isPending}>
              {createMenu.isPending ? 'Creating…' : 'Create'}
            </button>
            {createMenu.error ? (
              <p className="error">{errorMessage(createMenu.error, 'could not create menu')}</p>
            ) : null}
          </form>
        </section>
      ) : null}

      <section>
        <h2>All menus</h2>
        {menus.isLoading ? <p>Loading…</p> : null}
        {menus.error ? (
          <p className="error">{errorMessage(menus.error, 'could not load menus')}</p>
        ) : null}
        {menus.data ? (
          menus.data.menus.length === 0 ? (
            <p className="muted">
              {canEdit
                ? 'No menus yet. Create one above.'
                : 'No menus yet. Ask an owner to create one.'}
            </p>
          ) : (
            <ul className="menu-list">
              {menus.data.menus.map((m) => (
                <li key={m.id} className="card menu-row">
                  <div>
                    <h3>{m.name}</h3>
                    <p className="muted">
                      {m.slug} · v{m.version} · {m.state}
                    </p>
                  </div>
                  <div className="row">
                    <Link className="button" to={`/orgs/${orgId}/menus/${m.id}`}>
                      {canEdit ? 'Edit' : 'View'}
                    </Link>
                    {canPublish ? (
                      <button
                        type="button"
                        disabled={publish.isPending}
                        onClick={() => publish.mutate({ menuId: m.id, expectedVersion: m.version })}
                      >
                        {publish.isPending && publish.variables?.menuId === m.id
                          ? 'Publishing…'
                          : 'Publish'}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : null}
        {publish.error ? (
          <p className="error">{errorMessage(publish.error, 'could not publish')}</p>
        ) : null}
      </section>
    </main>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return fallback;
  }
  if (err.status === 403) return 'You do not have permission to do that.';
  if (err.status === 409) {
    // Common publish/rollback races: version_conflict, rollback_no_previous.
    if (err.message.toLowerCase().includes('rollback_no_previous')) {
      return 'There is nothing to roll back to.';
    }
    if (err.message.toLowerCase().includes('version')) {
      return 'Someone else edited this first — reload and try again.';
    }
    return err.message;
  }
  if (err.status === 0) return 'Network error — check your connection.';
  return err.message || fallback;
}
